import { memo, useState, useEffect, useCallback, useRef, forwardRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isArray, noop, isEmpty, isNil, values, cloneDeep } from 'lodash';
// Import Types
import type { ListHeaderConfig, TemplateListItem } from 'types';
import type { UseListFiltersReturnValue } from 'hooks/useListFilters';
// Import UI Components
import DataTable, { type DataTableRefActions } from 'components/common/DataTable';
import TemplateActionsModals from './TemplateActionsModals';
import TemplateActionsButtons from './TemplateActionsButtons';
// Import Hooks
import useGoToRoute from 'hooks/GoToRoute';
import useToaster from 'hooks/UseToaster';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useQueryParams from 'hooks/UseQueryParams';
import useHasPermission from 'hooks/UseHasPermission';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import { useMenuItem } from 'hooks/UseMenuItem';
// Import Helpers
import { getPageNumber } from 'services/Helpers';
// Import Services
import { TemplateHttpService, UtilsHttpService } from 'services/http';
// Import Configs
import { dataTableColumnsConfigs } from './configs';
// Import Constants
import {
    ModalsActions,
    StateModes,
    TargetTypeEnum,
    l,
    TemplateTypesLabels,
    AlertTypes,
    FilterComparisons,
    PARTNER_SETTINGS,
} from 'constants/common';
import { RoutesList } from 'routes';
// Import Enums
import { DeliveryMethodEnum, MenuItemIdEnum } from 'enums';
// Import Styles
import 'assets/scss/templateListPage.scss';

const { warning } = AlertTypes;

const {
    TEMPLATE_CLONE_EMAIL,
    TEMPLATE_CLONE_SMS,
    TEMPLATE_CLONE_HOORY,
    TEMPLATE_CLONE_PREDEFINED_HOORY,
    TEMPLATE_CLONE_INTERNAL_MESSAGE,
    TEMPLATE_CLONE_SOCIAL_TELEGRAM,
    TEMPLATE_CLONE_PUSH_NOTIFICATION,
    TEMPLATE_CLONE_POP_UP_NOTIFICATION,
    TEMPLATE_CLONE_DYNAMIC_POP_UP,
    TEMPLATE_CLONE_CMS_POP_UP_NOTIFICATION,
    TEMPLATE_CLONE_CMS_BANNER,
    TEMPLATE_CLONE_SOCIAL_VIBER,
    TEMPLATE_CLONE_SOCIAL_WHATS_APP,
    TEMPLATE_CLONE_SOCIAL_WHATS_APP_PROVIDER,
    TEMPLATE_CLONE_PREDEFINED_EMAIL,
    TEMPLATE_CLONE_PREDEFINED_SMS,
    TEMPLATE_CLONE_PREDEFINED_INTERNAL_MESSAGE,
    TEMPLATE_CLONE_SOCIAL_PREDEFINED_TELEGRAM,
    TEMPLATE_CLONE_PREDEFINED_PUSH_NOTIFICATION,
    TEMPLATE_CLONE_PREDEFINED_DYNAMIC_POP_UP,
    TEMPLATE_CLONE_SOCIAL_PREDEFINED_VIBER,
    TEMPLATE_CLONE_PREDEFINED_SOCIAL_WHATS_APP_PROVIDER,
    TEMPLATE_EDIT_EMAIL,
    TEMPLATE_EDIT_SMS,
    TEMPLATE_EDIT_HOORY,
    TEMPLATE_EDIT_INTERNAL_MESSAGE,
    TEMPLATE_EDIT_SOCIAL_TELEGRAM,
    TEMPLATE_EDIT_SOCIAL_WHATS_APP,
    TEMPLATE_EDIT_SOCIAL_WHATS_APP_PROVIDER,
    TEMPLATE_EDIT_PUSH_NOTIFICATION,
    TEMPLATE_EDIT_POP_UP_NOTIFICATION,
    TEMPLATE_EDIT_DYNAMIC_POP_UP,
    TEMPLATE_EDIT_CMS_POP_UP_NOTIFICATION,
    TEMPLATE_EDIT_CMS_BANNER,
    TEMPLATE_EDIT_SOCIAL_VIBER,
} = RoutesList;

interface Props {
    tableKey?: string;
    isViewMode?: boolean;
    isHasRowMultiSelect?: boolean;
    isHasRowRadioSelect?: boolean;
    isHasBulkActions?: boolean;
    isShowColumnChooser?: boolean;
    withManageLabels?: boolean;
    withQueryParams?: boolean;
    defaultRadioSelectedRow?: Nullable<AnyObject>;
    getRadioSelectedRow?: () => AnyObject;
    getHeaderActions?: () => JSX.Element;
    dataTableClassNames?: string;
    getTitle?: (row: AnyObject) => string;
    radioSelectColumnConfig?: AnyObject;
    isVisibleHeader?: boolean;
    isInCampaignPage?: boolean;
    listFilters: UseListFiltersReturnValue;
    /** Compact single-row header; when set, `getHeaderActions` is ignored. */
    headerConfig?: ListHeaderConfig;
}

const TemplateList = forwardRef<DataTableRefActions, Props>((props, ref) => {
    const {
        getTitle,
        isViewMode = false,
        getHeaderActions = noop,
        withManageLabels = false,
        isHasRowMultiSelect = false,
        isHasRowRadioSelect = false,
        isHasBulkActions = false,
        isShowColumnChooser = true,
        getRadioSelectedRow = noop,
        defaultRadioSelectedRow = null,
        tableKey = 'templateListDataTable',
        withQueryParams = false,
        dataTableClassNames = 'default-height',
        radioSelectColumnConfig,
        isVisibleHeader,
        isInCampaignPage = false,
        listFilters,
        headerConfig,
    } = props;

    const { t } = useTranslation();
    const { getRoutePath, goToPath } = useGoToRoute();
    const { search } = useLocation();
    const { showToaster } = useToaster();
    const { doPostRequest, doGetRequest } = useRequest();

    const dateFormats = usePartnerDateFormats();
    const { hasPartnerSettings } = useHasPermission();

    const showTemplateCategoryColumn = hasPartnerSettings(PARTNER_SETTINGS.IsAllowTemplateCategory);

    const { isMounted, baseRequestBody, changePageNumber, changePageSize, changeSorting } = listFilters;

    const [isTemplateListLoading, setIsTemplateListLoading] = useState(false);
    const [templateListDataCount, setTemplateListDataCount] = useState(0);
    const templateListDataRef = useRef<TemplateListItem[]>([]);

    const [isDeleteModalOpenedState, setIsDeleteModalOpenedState] = useState(false);
    const [isTestTemplateModalOpenedState, setIsTestTemplateModalOpenedState] = useState(false);
    const [isArchiveModalOpenedState, setIsArchiveModalOpenedState] = useState(false);
    const [isUnArchiveModalOpenedState, setIsUnArchiveModalOpenedState] = useState(false);
    const [isInfoModalOpenedState, setIsInfoModalOpenedState] = useState(false);
    const [isUsedInConfirmationModalOpenedState, setIsUsedInConfirmationModalOpenedState] = useState(false);
    const [openedModalData, setOpenedModalData] = useState<AnyObject>({});
    const [providers, setProviders] = useState({});

    const { convertedData: templateCategories, isLoading: isTemplateCategoriesLoading } = useMenuItem(
        MenuItemIdEnum.TemplateCategory,
    );

    // Template fetch part
    const { getTemplatesRequest, getCampaignsByTemplateRequest, getMessageProvidersRequest } = useCancelableRequests(
        {
            getTemplatesRequest: TemplateHttpService.getTemplateList,

            getCampaignsByTemplateRequest: TemplateHttpService.getCampaignsByTemplate,
            getMessageProvidersRequest: UtilsHttpService.getPartnerMessageProviders,
        },
        { takeLatest: ['getTemplatesRequest'] },
    );

    const goToRouteQueryParams = useQueryParams();

    const getTemplates = useCallback(
        (filter = baseRequestBody) => {
            const newFilters = [...cloneDeep(filter.Filters)];
            // when the campaignExecutor supports DynamicPopUp Template, this block must be deleted
            if (isInCampaignPage) {
                const deliveryMethodFieldIndex = newFilters.findIndex((elem) => elem.Name === 'DeliveryMethod');

                const notSupportedDeliveryMethods = [TemplateTypesLabels.DynamicPopUp];

                if (deliveryMethodFieldIndex === -1) {
                    newFilters.push({
                        Name: 'DeliveryMethod',
                        Comparision: FilterComparisons.NotIn,
                        Values: notSupportedDeliveryMethods,
                    });
                } else {
                    newFilters[deliveryMethodFieldIndex].Values = newFilters[deliveryMethodFieldIndex].Values.filter(
                        (value) => !notSupportedDeliveryMethods.includes(value),
                    );
                }

                if (deliveryMethodFieldIndex !== -1 && isEmpty(newFilters[deliveryMethodFieldIndex].Values)) {
                    newFilters[deliveryMethodFieldIndex].Comparision = FilterComparisons.NotIn;
                    newFilters[deliveryMethodFieldIndex].Values = notSupportedDeliveryMethods;
                }
            }

            setIsTemplateListLoading(true);
            doPostRequest(getTemplatesRequest.request, {
                requestBody: { ...filter, Filters: newFilters },
                successCallback: (Data) => {
                    templateListDataRef.current = Data?.Data ?? [];
                    setTemplateListDataCount(isNil(Data?.Count) ? 0 : Data.Count);
                },
            }).then(({ IsCanceled }) => {
                // A superseded call must not hide the loader of the call that replaced it
                if (!IsCanceled) {
                    setIsTemplateListLoading(false);
                }
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [baseRequestBody],
    );

    const getTableRows = (templateListData, templateCategories) => {
        const categoriesMap = templateCategories.reduce((acc, item) => {
            acc[item.value] = item.label;
            return acc;
        }, {});

        return templateListData.map(
            ({
                Name,
                IsUsed,
                CreatorName,
                CreatedDate,
                State,
                TemplateId,
                DeliveryMethod,
                Language,
                ModifiedDate,
                Status,
                CategoryId,
                CategoryName,
                CategoryColor,
                ChildItemsInfo,
                ArchivedDate,
                TemplateCategoryIds,
            }) => {
                return {
                    hasHover: true,
                    dragDisable: true,
                    nestedData: [],
                    className: !isNil(ArchivedDate) ? 'crm-data-table-row-archive' : '',
                    data: {
                        Name,
                        IsUsed,
                        'CreatedBy.Name': CreatorName,
                        CreatedDate,
                        State,
                        TemplateId,
                        DeliveryMethod,
                        Language,
                        ModifiedDate,
                        Status,
                        CategoryId,
                        CategoryName,
                        CategoryColor,
                        ChildItemsInfo,
                        ArchivedDate,
                        CategoryNames: TemplateCategoryIds.map((categoryId) => categoriesMap[categoryId] || ''),
                    },
                };
            },
        );
    };

    const templateListData = useMemo(() => {
        if (isTemplateCategoriesLoading || isTemplateListLoading) {
            return [];
        }

        return getTableRows(templateListDataRef.current, templateCategories);
    }, [isTemplateCategoriesLoading, isTemplateListLoading, templateCategories]);

    const getProviders = (channel) => {
        doPostRequest(getMessageProvidersRequest.request, {
            requestBody: { DeliveryMethod: channel },
            successCallback: (Data) => {
                setProviders((prev) => {
                    prev[channel] = (isNil(Data) ? [] : Data).reduce((acc, item) => {
                        acc[item.Id] = { label: item.Name, value: item.Id };
                        return acc;
                    }, {});
                    return { ...prev };
                });
            },
        });
    };

    const getEditTemplatePath = (template) => {
        const { DeliveryMethod, TemplateId } = template;
        const params = { templateId: TemplateId };
        let path;

        switch (DeliveryMethod) {
            case TemplateTypesLabels.Email:
                path = TEMPLATE_EDIT_EMAIL;
                break;
            case TemplateTypesLabels.SMS:
                path = TEMPLATE_EDIT_SMS;
                break;
            case TemplateTypesLabels.Hoory:
                path = TEMPLATE_EDIT_HOORY;
                break;
            case TemplateTypesLabels.Internal:
                path = TEMPLATE_EDIT_INTERNAL_MESSAGE;
                break;
            case TemplateTypesLabels.Telegram:
                path = TEMPLATE_EDIT_SOCIAL_TELEGRAM;
                break;
            case TemplateTypesLabels.PushNotification:
                path = TEMPLATE_EDIT_PUSH_NOTIFICATION;
                break;
            case TemplateTypesLabels.PopUp:
                path = TEMPLATE_EDIT_POP_UP_NOTIFICATION;
                break;
            case TemplateTypesLabels.DynamicPopUp:
                path = TEMPLATE_EDIT_DYNAMIC_POP_UP;
                break;
            case TemplateTypesLabels.CMSPopUp:
                path = TEMPLATE_EDIT_CMS_POP_UP_NOTIFICATION;
                break;
            case TemplateTypesLabels.CMSBanner:
                path = TEMPLATE_EDIT_CMS_BANNER;
                break;
            case TemplateTypesLabels.Viber:
                path = TEMPLATE_EDIT_SOCIAL_VIBER;
                break;
            case TemplateTypesLabels.WhatsApp:
                path = TEMPLATE_EDIT_SOCIAL_WHATS_APP;
                break;
            case TemplateTypesLabels.WhatsAppProvider:
                path = TEMPLATE_EDIT_SOCIAL_WHATS_APP_PROVIDER;
                break;
            default:
        }

        return getRoutePath(path, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const goToEditTemplate = (template) => {
        const path = getEditTemplatePath(template);
        goToPath(path);
    };

    // Actions part
    const deleteTemplateAction = (template) => {
        setOpenedModalData(template);
        setIsDeleteModalOpenedState(true);
    };

    const testTemplateAction = (template) => {
        if (
            (template.DeliveryMethod === TemplateTypesLabels.Email ||
                template.DeliveryMethod === TemplateTypesLabels.SMS) &&
            isEmpty(providers[template.DeliveryMethod])
        ) {
            showToaster(warning, t(l.ProviderIsNotIntegrated));
        } else {
            setOpenedModalData(template);
            setIsTestTemplateModalOpenedState(true);
        }
    };

    const archiveTemplateAction = (template) => {
        setOpenedModalData(template);
        setIsArchiveModalOpenedState(true);
    };

    const unarchiveTemplateAction = (template) => {
        setOpenedModalData(template);
        setIsUnArchiveModalOpenedState(true);
    };

    const infoTemplateAction = (template) => {
        setOpenedModalData(template);
        setIsInfoModalOpenedState(true);
    };

    const editTemplateAction = (template) => {
        doGetRequest(getCampaignsByTemplateRequest.request, {
            queryString: { templateId: template.TemplateId },
            successCallback: (Data) => {
                if (isEmpty(Data)) {
                    goToEditTemplate(template);
                } else {
                    setOpenedModalData({ objects: Data.map(({ Name, Type }) => ({ Name, Type })), template });
                    setIsUsedInConfirmationModalOpenedState(true);
                }
            },
        });
    };

    const getCloneTemplatePath = useCallback(
        (template) => {
            const { DeliveryMethod, TemplateId } = template;
            const params = { templateId: TemplateId };

            switch (DeliveryMethod) {
                case TemplateTypesLabels.Email:
                    return getRoutePath(TEMPLATE_CLONE_EMAIL, params);
                case TemplateTypesLabels.SMS:
                    return getRoutePath(TEMPLATE_CLONE_SMS, params);
                case TemplateTypesLabels.Hoory:
                    return getRoutePath(TEMPLATE_CLONE_HOORY, params);
                case TemplateTypesLabels.Internal:
                    return getRoutePath(TEMPLATE_CLONE_INTERNAL_MESSAGE, params);
                case TemplateTypesLabels.Telegram:
                    return getRoutePath(TEMPLATE_CLONE_SOCIAL_TELEGRAM, params);
                case TemplateTypesLabels.PushNotification:
                    return getRoutePath(TEMPLATE_CLONE_PUSH_NOTIFICATION, params);
                case TemplateTypesLabels.PopUp:
                    return getRoutePath(TEMPLATE_CLONE_POP_UP_NOTIFICATION, params);
                case TemplateTypesLabels.DynamicPopUp:
                    return getRoutePath(TEMPLATE_CLONE_DYNAMIC_POP_UP, params);
                case TemplateTypesLabels.CMSPopUp:
                    return getRoutePath(TEMPLATE_CLONE_CMS_POP_UP_NOTIFICATION, params);
                case TemplateTypesLabels.CMSBanner:
                    return getRoutePath(TEMPLATE_CLONE_CMS_BANNER, params);
                case TemplateTypesLabels.Viber:
                    return getRoutePath(TEMPLATE_CLONE_SOCIAL_VIBER, params);
                case TemplateTypesLabels.WhatsApp:
                    return getRoutePath(TEMPLATE_CLONE_SOCIAL_WHATS_APP, params);
                case TemplateTypesLabels.WhatsAppProvider:
                    return getRoutePath(TEMPLATE_CLONE_SOCIAL_WHATS_APP_PROVIDER, params);
                default:
            }
        },
        [getRoutePath],
    );

    const getClonePredefinedPath = (template) => {
        const { DeliveryMethod, TemplateId } = template;

        const params = { templateId: TemplateId };
        let path;

        switch (DeliveryMethod) {
            case DeliveryMethodEnum.Email: {
                path = TEMPLATE_CLONE_PREDEFINED_EMAIL;
                break;
            }
            case DeliveryMethodEnum.SMS: {
                path = TEMPLATE_CLONE_PREDEFINED_SMS;
                break;
            }
            case DeliveryMethodEnum.Internal: {
                path = TEMPLATE_CLONE_PREDEFINED_INTERNAL_MESSAGE;
                break;
            }
            case DeliveryMethodEnum.Telegram: {
                path = TEMPLATE_CLONE_SOCIAL_PREDEFINED_TELEGRAM;
                break;
            }
            case DeliveryMethodEnum.PushNotification: {
                path = TEMPLATE_CLONE_PREDEFINED_PUSH_NOTIFICATION;
                break;
            }
            case DeliveryMethodEnum.DynamicPopUp: {
                path = TEMPLATE_CLONE_PREDEFINED_DYNAMIC_POP_UP;
                break;
            }
            case DeliveryMethodEnum.Viber: {
                path = TEMPLATE_CLONE_SOCIAL_PREDEFINED_VIBER;
                break;
            }
            case DeliveryMethodEnum.Hoory: {
                path = TEMPLATE_CLONE_PREDEFINED_HOORY;
                break;
            }
            case DeliveryMethodEnum.WhatsAppProvider: {
                path = TEMPLATE_CLONE_PREDEFINED_SOCIAL_WHATS_APP_PROVIDER;
                break;
            }

            default:
        }

        return getRoutePath(path, params);
    };

    const { columns } = useMemo(
        () => dataTableColumnsConfigs(t, getCloneTemplatePath, dateFormats, showTemplateCategoryColumn),
        [dateFormats, getCloneTemplatePath, showTemplateCategoryColumn, t],
    );

    const modalsStateSetDict = {
        deleteTemplate: {
            key: 'deleteTemplate',
            fn: setIsDeleteModalOpenedState,
        },
        testTemplate: {
            key: 'testTemplate',
            fn: setIsTestTemplateModalOpenedState,
        },
        archiveTemplate: {
            key: 'archiveTemplate',
            fn: setIsArchiveModalOpenedState,
        },
        unarchiveTemplate: {
            key: 'unarchiveTemplate',
            fn: setIsUnArchiveModalOpenedState,
        },
        infoTemplate: {
            key: 'infoTemplate',
            fn: setIsInfoModalOpenedState,
        },
        editTemplate: {
            key: 'editTemplate',
            fn: setIsUsedInConfirmationModalOpenedState,
        },
    };

    const onCloseModalHandler = (modalStateKey, action, alertType, alertMessage, count = 1) => {
        const isTestTemplate = action === ModalsActions.TEST_TEMPLATE;

        if (
            action === ModalsActions.DELETE ||
            isTestTemplate ||
            action === ModalsActions.ARCHIVE ||
            action === ModalsActions.UNARCHIVE
        ) {
            if (!isTestTemplate && ref && 'current' in ref) {
                ref.current?.resetSelectedRows?.();
                ref.current?.changePaginationPageNumber?.(
                    getPageNumber(
                        baseRequestBody.Filters,
                        action,
                        templateListDataCount,
                        baseRequestBody.Pageing.PageSize,
                        baseRequestBody.Pageing.PageNumber,
                        count,
                    ),
                );
                getTableRefreshFn();
            }
            showToaster(alertType, alertMessage);
        }

        modalsStateSetDict[modalStateKey].fn(false);
    };

    const getRowActionBar = (row) => {
        const { data } = row;

        return (
            <>
                <TemplateActionsButtons
                    data={data}
                    isInfoVisible={true}
                    isViewMode={isViewMode}
                    testTemplateAction={testTemplateAction}
                    unarchiveTemplateAction={unarchiveTemplateAction}
                    archiveTemplateAction={archiveTemplateAction}
                    deleteTemplateAction={deleteTemplateAction}
                    getCloneTemplatePath={getCloneTemplatePath}
                    editTemplateAction={editTemplateAction}
                    getEditTemplatePath={getEditTemplatePath}
                    infoTemplateAction={infoTemplateAction}
                    getClonePredefinedPath={getClonePredefinedPath}
                />
            </>
        );
    };

    const getTableRefreshFn = () => {
        getTemplates(baseRequestBody);
    };

    const getDataTableName = () => <span>{t(l.Templates)}</span>;

    // Filter update part
    useEffect(() => {
        if (isMounted.current) {
            getTemplates(baseRequestBody);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseRequestBody]);

    const init = () => {
        getProviders(TemplateTypesLabels.SMS);
        getProviders(TemplateTypesLabels.Email);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    const isAllow = (template) => {
        const { IsUsed, ArchivedDate } = template;
        const isArchive = !isNil(ArchivedDate);

        return {
            delete: !IsUsed,
            archive: !isArchive,
            unarchive: isArchive,
        };
    };

    const getTemplatesNamesIds = (selectedRows) => {
        if (!isArray(selectedRows)) return;

        interface TemplatePermissionItem {
            templateId: number;
            name: string;
        }

        const allowDeleteTemplates: TemplatePermissionItem[] = [];
        const notAllowDeleteTemplates: TemplatePermissionItem[] = [];
        const allowArchiveTemplates: TemplatePermissionItem[] = [];
        const notAllowArchiveTemplates: TemplatePermissionItem[] = [];
        const allowUnarchiveTemplates: TemplatePermissionItem[] = [];
        const notAllowUnarchiveTemplates: TemplatePermissionItem[] = [];
        selectedRows.forEach(({ data }) => {
            const { TemplateId, Name } = data;
            const isAllowData = isAllow(data);
            if (isAllowData.delete) {
                allowDeleteTemplates.push({ templateId: TemplateId, name: Name });
            } else {
                notAllowDeleteTemplates.push({ templateId: TemplateId, name: Name });
            }
            if (isAllowData.archive) {
                allowArchiveTemplates.push({ templateId: TemplateId, name: Name });
            } else {
                notAllowArchiveTemplates.push({ templateId: TemplateId, name: Name });
            }

            if (isAllowData.unarchive) {
                allowUnarchiveTemplates.push({ templateId: TemplateId, name: Name });
            } else {
                notAllowUnarchiveTemplates.push({ templateId: TemplateId, name: Name });
            }
        });

        return {
            allowDeleteTemplates,
            notAllowDeleteTemplates,
            allowArchiveTemplates,
            notAllowArchiveTemplates,
            allowUnarchiveTemplates,
            notAllowUnarchiveTemplates,
        };
    };

    const deleteSelectedRows = (selectedRows) => {
        const templatesNamesIds = getTemplatesNamesIds(selectedRows);
        const namesIds = {
            allowDeleteTemplates: templatesNamesIds?.allowDeleteTemplates,
            notAllowDeleteTemplates: templatesNamesIds?.notAllowDeleteTemplates,
        };

        deleteTemplateAction(namesIds);
    };

    const archiveSelectedRows = (selectedRows) => {
        const templatesNamesIds = getTemplatesNamesIds(selectedRows);
        const namesIds = {
            allowArchiveTemplates: templatesNamesIds?.allowArchiveTemplates,
            notAllowArchiveTemplates: templatesNamesIds?.notAllowArchiveTemplates,
        };
        archiveTemplateAction(namesIds);
    };

    const unarchiveSelectedRows = (selectedRows) => {
        const templatesNamesIds = getTemplatesNamesIds(selectedRows);
        const namesIds = {
            allowUnarchiveTemplates: templatesNamesIds?.allowUnarchiveTemplates,
            notAllowUnarchiveTemplates: templatesNamesIds?.notAllowUnarchiveTemplates,
        };
        unarchiveTemplateAction(namesIds);
    };

    return (
        <>
            <DataTable
                ref={ref}
                classNames={dataTableClassNames}
                getTitle={getTitle}
                rowKey="TemplateId"
                columnKey="dataKey"
                name={getDataTableName()}
                tableKey={tableKey}
                isColumnsSortable={true}
                withPagination={true}
                data={templateListData}
                dataCount={templateListDataCount}
                rowActionBar={getRowActionBar}
                withManageLabels={withManageLabels}
                setLabelObjectIdKey={'TemplateId'}
                labelNameKey={'CategoryName'}
                labelColorKey={'CategoryColor'}
                labelObjectType={TargetTypeEnum.Template}
                columns={columns}
                isLoading={isTemplateListLoading || isTemplateCategoriesLoading}
                isShowRefreshButton={true}
                isShowColumnChooser={isShowColumnChooser}
                isShowVerticalDots={false}
                verticalDotsContentList={StateModes}
                setPagingPageSize={changePageSize}
                setPagingPageNumber={changePageNumber}
                sorting={baseRequestBody.Sorting}
                setSorting={changeSorting}
                onRefreshClick={getTableRefreshFn}
                headerActions={isNil(headerConfig) ? getHeaderActions() : undefined}
                headerConfig={headerConfig}
                isHasRowMultiSelect={isHasRowMultiSelect}
                isHasBulkActions={isHasBulkActions}
                isHasRowRadioSelect={isHasRowRadioSelect}
                getRadioSelectedRow={getRadioSelectedRow}
                archiveSelectedRows={archiveSelectedRows}
                unarchiveSelectedRows={unarchiveSelectedRows}
                deleteSelectedRows={deleteSelectedRows}
                defaultRadioSelectedRow={defaultRadioSelectedRow}
                receivedCurrentPageNumber={baseRequestBody.Pageing.PageNumber}
                withQueryParams={withQueryParams}
                isVisibleHeader={isVisibleHeader}
                radioSelectColumnConfig={radioSelectColumnConfig}
            />

            <TemplateActionsModals
                isDeleteModalOpenedState={isDeleteModalOpenedState}
                isArchiveModalOpenedState={isArchiveModalOpenedState}
                isUnArchiveModalOpenedState={isUnArchiveModalOpenedState}
                isInfoModalOpenedState={isInfoModalOpenedState}
                isUsedInConfirmationModalOpenedState={isUsedInConfirmationModalOpenedState}
                isTestTemplateModalOpenedState={isTestTemplateModalOpenedState}
                onCloseModalHandler={onCloseModalHandler}
                modalsStateSetDict={modalsStateSetDict}
                openedModalData={openedModalData}
                goToEditTemplate={goToEditTemplate}
                providers={values(providers[openedModalData.DeliveryMethod])}
            />
        </>
    );
});

TemplateList.displayName = 'TemplateList';

export default memo(TemplateList);
